import { useLocation } from "wouter";
import { motion } from "framer-motion";
import { PageMeta } from "@/components/PageMeta";

export function MissionDebrief() {
  const [, setLocation] = useLocation();
  const navToSignIn = () => setLocation("/sign-in");

  return (
    <div className="min-h-screen bg-[#030913] text-white font-sans selection:bg-[#7C6EEF]/30 pb-20">
      <PageMeta
        title="Mission Debrief — Customer Results — LaunchTactix"
        description="Real growth results from LaunchTactix deployments: 14+ meetings booked per month, pipeline growth, and full growth-team replacement case studies."
        canonical="https://launchtactix.com/mission-debrief"
      />
      <nav className="sticky top-0 z-50 bg-[#030913]/90 backdrop-blur-md border-b border-white/5 flex items-center justify-between px-6 py-4">
        <button onClick={() => setLocation("/")} className="hover:opacity-80 transition-opacity">
          <img src={`${import.meta.env.BASE_URL}images/logo-wordmark.png`} alt="LaunchTactix" width={606} height={216} className="h-8 w-auto mix-blend-screen" />
        </button>
        <button
          onClick={navToSignIn}
          className="text-xs font-bold tracking-widest text-white/70 hover:text-white transition-colors uppercase px-5 py-2 border border-white/10 hover:bg-white/5 rounded-none"
        >
          INITIATE
        </button>
      </nav>

      <main className="max-w-4xl mx-auto px-6 pt-20">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-24 text-center"
        >
          <p className="font-mono text-sm tracking-widest text-[#7C6EEF] mb-6 uppercase">// CASE STUDY</p>
          <h1 className="text-5xl md:text-7xl font-display font-bold uppercase tracking-tight mb-8">MISSION DEBRIEF</h1>
          <p className="text-lg text-white/60 leading-relaxed max-w-3xl mx-auto">
            Here's what happens when you plug in LaunchTactix — autonomous AI outreach and lead generation running 24/7 — and scale your pipeline without adding headcount.
          </p>
          <div className="mt-10">
            <button
              onClick={navToSignIn}
              className="inline-flex items-center justify-center gap-3 px-8 py-4 bg-[#7C6EEF] text-white font-display font-bold tracking-widest uppercase hover:bg-white hover:text-black transition-colors rounded-none"
            >
              LAUNCH ENGINE →
            </button>
          </div>
        </motion.div>

        {/* SECTION 1 — THE CHALLENGE */}
        <motion.section
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-24"
        >
          <div className="mb-12 border-b border-white/10 pb-4">
            <p className="text-xs font-mono text-[#7C6EEF] uppercase tracking-widest mb-2">THE CHALLENGE</p>
            <h2 className="text-3xl font-display font-bold uppercase text-white/90">THE MANUAL GROWTH.</h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-white/[0.02] border border-white/10 p-8">
              <h3 className="text-sm font-display font-bold uppercase tracking-widest text-[#7C6EEF] mb-4">1. BIG BUILDS vs. LIST BUILDING</h3>
              <p className="text-sm text-white/60 leading-relaxed">
                Building a qualified lead list manually meant hours of research, LinkedIn prospecting, and cross-referencing company databases — just to get to a cold list that still needed validation. Most teams were spending 40–60% of their growth time on research that could never scale.
              </p>
            </div>
            <div className="bg-white/[0.02] border border-white/10 p-8">
              <h3 className="text-sm font-display font-bold uppercase tracking-widest text-[#7C6EEF] mb-4">2. GENERIC OUTREACH. FLAT REPLIES.</h3>
              <p className="text-sm text-white/60 leading-relaxed">
                Without personalization infrastructure, outreach defaulted to template blasts. Low reply rates, high unsubscribes, and a growing suppression list. The harder teams pushed volume, the worse deliverability got — a death spiral that killed most outreach programs within 90 days.
              </p>
            </div>
            <div className="bg-white/[0.02] border border-white/10 p-8">
              <h3 className="text-sm font-display font-bold uppercase tracking-widest text-[#7C6EEF] mb-4">3. GROWTH RATES AT HEADCOUNT.</h3>
              <p className="text-sm text-white/60 leading-relaxed">
                Every percentage point of growth required more headcount. More SDRs, more researchers, more content writers, more social managers. The unit economics broke down at scale — the cost of growth was growing faster than revenue.
              </p>
            </div>
            <div className="bg-white/[0.02] border border-white/10 p-8">
              <h3 className="text-sm font-display font-bold uppercase tracking-widest text-[#7C6EEF] mb-4">4. NO SINGLE SOURCE OF TRUTH.</h3>
              <p className="text-sm text-white/60 leading-relaxed">
                Leads lived in spreadsheets. Deals lived in one CRM. Emails went through a separate sequencing tool. Social was managed in another platform. Attribution was impossible — teams couldn't connect outreach activity to closed revenue, making it impossible to optimize.
              </p>
            </div>
          </div>
        </motion.section>

        {/* SECTION 2 — THE SOLUTION */}
        <motion.section
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-24"
        >
          <div className="mb-12 border-b border-white/10 pb-4">
            <p className="text-xs font-mono text-[#7C6EEF] uppercase tracking-widest mb-2">THE SOLUTION</p>
            <h2 className="text-3xl font-display font-bold uppercase text-white/90">IMPLEMENTING GROWTH: 24/7 INFRASTRUCTURE.</h2>
          </div>

          <div className="space-y-6">
            <div className="bg-[#7C6EEF]/5 border border-[#7C6EEF]/20 p-8 md:p-10 flex flex-col md:flex-row gap-6 items-start">
              <div className="text-[#7C6EEF] font-mono text-xl font-bold shrink-0">01</div>
              <div>
                <h3 className="text-lg font-display font-bold uppercase tracking-wide text-white mb-3">LEAD DISCOVERY</h3>
                <p className="text-white/60 leading-relaxed text-sm">
                  LaunchTactix autonomously discovers thousands of qualified leads daily — matching your ICP, verified, scored, and enriched with contact data, firmographics, and intent signals. Zero manual research. The engine runs continuously, feeding a live pipeline of high-confidence prospects.
                </p>
              </div>
            </div>
            
            <div className="bg-[#7C6EEF]/5 border border-[#7C6EEF]/20 p-8 md:p-10 flex flex-col md:flex-row gap-6 items-start">
              <div className="text-[#7C6EEF] font-mono text-xl font-bold shrink-0">02</div>
              <div>
                <h3 className="text-lg font-display font-bold uppercase tracking-wide text-white mb-3">AI-PERSONALIZED OUTREACH</h3>
                <p className="text-white/60 leading-relaxed text-sm">
                  Every email is AI-generated and personalized to the individual prospect — their role, company, industry, and live signals. Multi-touch sequences run automatically, with A/B testing, mailbox rotation, and send-time optimization built in. Reply handling is AI-assisted: interested replies are flagged and escalated immediately.
                </p>
              </div>
            </div>

            <div className="bg-[#7C6EEF]/5 border border-[#7C6EEF]/20 p-8 md:p-10 flex flex-col md:flex-row gap-6 items-start">
              <div className="text-[#7C6EEF] font-mono text-xl font-bold shrink-0">03</div>
              <div>
                <h3 className="text-lg font-display font-bold uppercase tracking-wide text-white mb-3">ENRICHMENT & VERIFICATION</h3>
                <p className="text-white/60 leading-relaxed text-sm">
                  Every lead is continuously enriched and re-verified before each send. Email validation, SMTP verification, intent signal scoring, and ICP fit scoring all run in the background. Bad data is suppressed automatically. The pipeline stays clean without any manual intervention.
                </p>
              </div>
            </div>

            <div className="bg-[#7C6EEF]/5 border border-[#7C6EEF]/20 p-8 md:p-10 flex flex-col md:flex-row gap-6 items-start">
              <div className="text-[#7C6EEF] font-mono text-xl font-bold shrink-0">04</div>
              <div>
                <h3 className="text-lg font-display font-bold uppercase tracking-wide text-white mb-3">DEAL PIPELINE & ATTRIBUTION</h3>
                <p className="text-white/60 leading-relaxed text-sm">
                  Every meeting booked flows directly into the CRM. Every deal is linked back to its originating outreach sequence. Attribution is automatic — you can see exactly which sequences generated which revenue. Pipeline analytics update in real time.
                </p>
              </div>
            </div>
          </div>
        </motion.section>

        {/* SECTION 3 — RESULTS */}
        <motion.section
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <div className="mb-16 text-center">
            <h2 className="text-3xl font-display font-bold uppercase text-white/90 mb-4">GROWTH TEAM: AUTONOMOUS.</h2>
            <p className="text-white/50 text-lg">The numbers after deploying LaunchTactix.</p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-5 gap-8 mb-20">
            <div className="text-center">
              <div className="text-4xl font-display font-bold text-white mb-2">1</div>
              <div className="text-[10px] font-mono tracking-widest text-[#7C6EEF] uppercase">team member managing it all</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-display font-bold text-white mb-2">24/7</div>
              <div className="text-[10px] font-mono tracking-widest text-[#7C6EEF] uppercase">autonomous operation</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-display font-bold text-white mb-2">14+</div>
              <div className="text-[10px] font-mono tracking-widest text-[#7C6EEF] uppercase">qualified meetings/month</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-display font-bold text-white mb-2">14+</div>
              <div className="text-[10px] font-mono tracking-widest text-[#7C6EEF] uppercase">new pipeline deals/month</div>
            </div>
            <div className="text-center md:col-span-1 col-span-2">
              <div className="text-4xl font-display font-bold text-white mb-2">70+</div>
              <div className="text-[10px] font-mono tracking-widest text-[#7C6EEF] uppercase">AI modules running</div>
            </div>
          </div>

          <div className="bg-[#7C6EEF] p-10 md:p-16 text-center text-black">
            <p className="text-xl md:text-2xl font-display font-bold uppercase leading-tight max-w-2xl mx-auto">
              "WE STOPPED HIRING TO SCALE. LAUNCHTACTIX RUNS PERSONALIZED AI OUTREACH AROUND THE CLOCK AND OUR PIPELINE GREW WITHOUT GROWING OUR HEADCOUNT."
            </p>
          </div>
        </motion.section>

        {/* BOTTOM CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mt-32 text-center border-t border-white/5 pt-20"
        >
          <h2 className="text-4xl md:text-5xl font-display font-bold uppercase tracking-tight mb-6">SYSTEM READY.</h2>
          <p className="text-lg text-white/50 mb-10">Your pipeline is waiting. Deploy your autonomous growth infrastructure.</p>
          <button
            onClick={navToSignIn}
            className="inline-flex items-center justify-center gap-3 px-8 py-4 bg-[#7C6EEF] text-white font-display font-bold tracking-widest uppercase hover:bg-white hover:text-black transition-colors rounded-none"
          >
            LAUNCH ENGINE →
          </button>
        </motion.div>
      </main>

      <footer className="mt-32 py-12 px-6 border-t border-white/5 bg-black">
        <div className="max-w-4xl mx-auto flex flex-col items-center gap-6">
          <img src={`${import.meta.env.BASE_URL}images/logo-wordmark.png`} alt="LaunchTactix" width={606} height={216} className="h-6 w-auto mix-blend-screen opacity-50" />
          <p className="text-[10px] font-mono tracking-widest text-white/30 uppercase">© {new Date().getFullYear()} LaunchTactix. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
